import { Injectable, computed, inject, signal } from '@angular/core';
import { finalize, map, Observable, of, shareReplay } from 'rxjs';
import { UserModel } from '@models/user/user.model';
import { UserService } from './user.service';

const STORAGE_KEY = 'customer_cache';
const STORAGE_KEY_TS = 'customer_cache_ts';
const CACHE_AGE = 60 * 1000 * 5; // 5 minutes

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  private readonly userService = inject(UserService);

  private readonly customer = signal<UserModel | null>(this.#readStorage());
  private readonly customerLastUpdate = signal<number>(
    Number(localStorage.getItem(STORAGE_KEY_TS) ?? 0),
  );
  private inFlight$: Observable<UserModel[]> | null = null;

  readonly currentCustomer = computed(() => {
    return this.customer();
  });

  getCustomer(identifier: string | null): Observable<UserModel | null> {
    if (this.customerLastUpdate() > Date.now() - CACHE_AGE || !identifier) {
      return of(this.customer());
    }
    return this.searchCustomer(identifier).pipe(map((customers) => customers[0] ?? null));
  }

  searchCustomer(identifier: string): Observable<UserModel[]> {
    if (!this.inFlight$) {
      this.inFlight$ = this.userService.searchUser(identifier).pipe(
        finalize(() => {
          this.inFlight$ = null;
        }),
        shareReplay(1),
      );
    }
    return this.inFlight$;
  }

  selectCustomer(customer: UserModel): void {
    this.customer.set(customer);
    this.customerLastUpdate.set(Date.now());
    localStorage.setItem(STORAGE_KEY, JSON.stringify(customer));
    localStorage.setItem(STORAGE_KEY_TS, String(Date.now()));
  }

  #readStorage(): UserModel | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as UserModel) : null;
    } catch {
      return null;
    }
  }
}
